import React, { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { useCta } from "@/context/SettingsContext";
import { Btn } from "@/components/ui-kit/Kit";
import { MaskLines } from "@/components/motion/Reveal";
import { GpuMark } from "@/components/brand/GpuLogo";
import { IMAGES } from "@/config/images";

export const Hero = () => {
  const { t } = useI18n();
  const cta = useCta();
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.04, 1.16]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);
  const sealY = useTransform(scrollYProgress, [0, 1], ["0%", "-38%"]);
  const sealRotate = useTransform(scrollYProgress, [0, 1], [0, 16]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[88vh] overflow-hidden bg-navy-deep text-white grain sm:min-h-[92vh]"
    >
      <motion.div
        style={reduce ? undefined : { y: imgY, scale: imgScale }}
        className="absolute inset-0"
        aria-hidden="true"
      >
        <img
          src={IMAGES.heroWarehouse.src}
          alt=""
          className="h-full w-full object-cover opacity-[0.34]"
          loading="eager"
        />
      </motion.div>
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-[linear-gradient(105deg,#060F1E_0%,rgba(6,15,30,0.94)_38%,rgba(6,15,30,0.55)_100%)]"
      />
      <div aria-hidden="true" className="absolute inset-0 hairline-grid opacity-70" />

      <motion.div
        aria-hidden="true"
        style={reduce ? undefined : { y: sealY, rotate: sealRotate }}
        className="pointer-events-none absolute -right-24 top-1/2 hidden w-[460px] -translate-y-1/2 opacity-[0.09] lg:block xl:-right-10 xl:w-[540px]"
      >
        <img src="/gpu-logo-t-512.webp" alt="" className="h-auto w-full" loading="lazy" />
      </motion.div>

      <motion.div
        style={reduce ? undefined : { y: textY }}
        className="relative container flex min-h-[88vh] flex-col justify-center pb-20 pt-16 sm:min-h-[92vh] sm:pb-24 sm:pt-24"
      >
        <motion.div
          initial={reduce ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7 }}
          className="flex items-center gap-3"
        >
          <GpuMark className="h-11 w-11 sm:h-14 sm:w-14" size="sm" onDark priority />
          <span className="overline text-gold">{t("home.hero.eyebrow")}</span>
        </motion.div>

        <h1 className="mt-8 max-w-[15ch] font-display text-[clamp(2.6rem,7.4vw,6.5rem)] font-bold leading-[0.94] tracking-[-0.03em]">
          <MaskLines
            lines={[t("home.hero.line1"), t("home.hero.line2"), t("home.hero.line3")]}
            delay={0.2}
            lineClassName=""
          />
        </h1>

        <motion.div
          initial={reduce ? false : { opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85 }}
          className="mt-9 grid max-w-5xl gap-8 lg:grid-cols-[1.25fr_1fr] lg:items-end"
        >
          <p className="max-w-xl border-l border-gold/50 pl-6 text-sm leading-relaxed text-white/70 sm:text-base">
            {t("home.hero.sub")}
          </p>
          <div className="flex flex-wrap gap-3">
            <Btn href={cta.member_registration_url || "#"} external variant="solid" data-testid="hero-join-gpu">
              {t("cta.joinGpu")} <ArrowUpRight className="h-4 w-4" />
            </Btn>
            <Btn href={cta.vendor_registration_url || "#"} external variant="ghostLight" data-testid="hero-register-vendor">
              {t("cta.registerVendor")}
            </Btn>
            <Btn to="/about" variant="link" className="ml-1 self-center text-gold" data-testid="hero-know-more">
              {t("cta.knowMore")}
            </Btn>
          </div>
        </motion.div>

        <motion.div
          initial={reduce ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="mt-16 flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-white/10 pt-6"
        >
          <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-white/40">
            {t("home.hero.badge")}
          </span>
          <span className="ml-auto hidden items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-white/40 sm:flex">
            <ArrowDown className="h-3 w-3 animate-bounce" /> {t("home.hero.scroll")}
          </span>
        </motion.div>
      </motion.div>
    </section>
  );
};
